// Volitiq Environment Configuration
//
// Environment variables are loaded from:
// - .env.local for local development (gitignored)
// - .env.development for `npm run dev` builds (dev cloud)
// - .env.production for production builds
//
// All VITE_* variables MUST be set at build time. `getConfig()` throws if any
// required value is missing, so a misconfigured environment fails fast rather
// than silently using stale defaults.

type RawConfig = {
  apiUrl: string;
  userPoolId: string;
  clientId: string;
  cognitoDomain: string;
  bucket: string;
  region: string;
  redirectSignIn: string;
  redirectSignOut: string;
};

function readEnv(name: string): string {
  const value = import.meta.env[name];
  return typeof value === 'string' ? value : '';
}

function readRawConfig(): RawConfig {
  return {
    apiUrl: readEnv('VITE_API_URL'),
    userPoolId: readEnv('VITE_COGNITO_USER_POOL_ID'),
    clientId: readEnv('VITE_COGNITO_CLIENT_ID'),
    cognitoDomain: readEnv('VITE_COGNITO_DOMAIN'),
    bucket: readEnv('VITE_S3_BUCKET'),
    region: readEnv('VITE_AWS_REGION'),
    redirectSignIn: readEnv('VITE_REDIRECT_SIGN_IN'),
    redirectSignOut: readEnv('VITE_REDIRECT_SIGN_OUT'),
  };
}

function assertRequired(raw: RawConfig): void {
  const missing: string[] = [];
  if (!raw.apiUrl) missing.push('VITE_API_URL');
  if (!raw.userPoolId) missing.push('VITE_COGNITO_USER_POOL_ID');
  if (!raw.clientId) missing.push('VITE_COGNITO_CLIENT_ID');
  if (!raw.cognitoDomain) missing.push('VITE_COGNITO_DOMAIN');
  if (!raw.bucket) missing.push('VITE_S3_BUCKET');
  if (!raw.region) missing.push('VITE_AWS_REGION');

  if (missing.length > 0) {
    throw new Error(
      `Volitiq frontend is missing required environment variables: ${missing.join(', ')}. ` +
        'Copy frontend/.env.local.example to frontend/.env.local and populate values, ' +
        'or run scripts/env-from-stack.sh after the backend is deployed.'
    );
  }
}

export type AppConfig = {
  production: boolean;
  apiUrl: string;
  cognito: {
    userPoolId: string;
    clientId: string;
    domain: string;
    region: string;
    redirectSignIn: string;
    redirectSignOut: string;
  };
  s3: {
    bucket: string;
    region: string;
  };
};

let cachedConfig: AppConfig | null = null;

export function getConfig(): AppConfig {
  if (cachedConfig) return cachedConfig;

  const raw = readRawConfig();
  assertRequired(raw);

  cachedConfig = {
    production: import.meta.env.PROD,
    apiUrl: raw.apiUrl,
    cognito: {
      userPoolId: raw.userPoolId,
      clientId: raw.clientId,
      domain: raw.cognitoDomain,
      region: raw.region,
      redirectSignIn: raw.redirectSignIn,
      redirectSignOut: raw.redirectSignOut,
    },
    s3: {
      bucket: raw.bucket,
      region: raw.region,
    },
  };

  return cachedConfig;
}

// Backwards-compatible eager export. Throws at module load if env is missing —
// preferred behaviour for production where misconfig should fail loudly.
export const config = getConfig();

// AWS Amplify configuration — direct sign-in (no OAuth)
export const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.clientId,
    },
  },
};
